import { useState, useRef, useEffect, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  health, getDrawing, exportBalloonPdf, uploadAsync, fetchMe, logout as apiLogout,
  fetchDrawingPartNo,
} from './api/client';
import { useUI } from './store/ui';
import { useAuthStore, useIsAdmin, useCanEdit } from './store/auth';
import { roleLabel } from './lib/auth';
import { loadNav, saveNav, type NavView } from './lib/navState';
import { loadBalloonStyle } from './lib/balloonStyle';
import { isTypingTarget, isUndoCombo } from './lib/keys';
import { useUndoDelete } from './lib/useFeatureDelete';
import type { DrawingScope, DrawingSummary, Feature, RecognitionJob,
              SipEntry } from './types';
import { FeatureTable } from './components/FeatureTable';
import { FilterSummary } from './components/FilterSummary';
import { StatusBadge } from './components/StatusBadge';
import { Canvas } from './components/Canvas';
import { DrawingTree } from './components/DrawingTree';
import { DrawingPartChip } from './components/DrawingPartChip';
import { MeasureReportDialog } from './components/MeasureReportDialog';
// v0.85：上传前「先选物料」—— 归户前置，避免分析完白跑。
import { MaterialPickDialog } from './components/MaterialPickDialog';
import JobProgressPanel from './components/JobProgressPanel';
import { PartMasterPage } from './components/PartMasterPage';
import { SipPage } from './components/sip/SipPage';
// v0.84（P3）：检验任务列表 —— 依据 SIP 建单，明细为建单时的快照。
import { InspectionTasksPage } from './components/InspectionTasksPage';
import { LoginPage } from './components/LoginPage';
import { UserAdminPage } from './components/UserAdminPage';
import { SettingsPage } from './components/SettingsPage';
import { useOptionsStore } from './store/options';
import { TrashPage } from './components/TrashPage';
import { PasswordDialog } from './components/PasswordDialog';
// v0.31（2026-10-03，用户要求）：工具条只保留「气泡 PDF」一个导出。
// 已移除的四个功能 —— 内部检验报告 / AS9102 FAI / 作业指导书 SIP / AI 标题栏 ——
// 与其面板组件、前端 API 封装、后端路由一并删除（按钮 → 面板 → 接口整链下线）。
// v0.32（2026-10-05，用户要求）：新增「测量报告」（自备 xlsx 模板注填）+ 图纸多级文件夹。
// v0.56（2026-10-16，用户要求）：新增「回收站」（全库：图纸/零件行/BOM/文件夹），
//   并把顶栏独立的「账号与权限」收进**用户按钮的下拉菜单**当设置。

export default function App() {
  const qc = useQueryClient();
  const { selectedDrawingId, selectDrawing, selectedFeaturePk,
          selectFeature, canvasPageIndex, setCanvasPage, toast, showToast } = useUI();
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  // V0.5.1：记住正在上传的文件名/是否栅格，用于"解析中"提示（大图 OCR 较慢，避免误以为卡死）
  const [uploadingName, setUploadingName] = useState<string | null>(null);
  /** v0.85：已选好但**还没上传**的文件 —— 等用户在弹窗里选定物料才真正上传。 */
  const [pendingUpload, setPendingUpload] = useState<File | null>(null);
  // V0.6：当前活动的识别任务（异步后台执行，前端轮询进度）
  const [activeJob, setActiveJob] = useState<RecognitionJob | null>(null);
  // v0.32：测量报告导出弹窗。
  // v0.52：从布尔改成「目标对象」—— 同一个弹窗现在有**两个唤起处**
  //   （图纸工作台工具条 + SIP 编辑页表头行），目标（图纸 id + 附图页码）
  //   由唤起方各自给定，弹窗自己拉预检数据。null = 关闭。
  const [mrTarget, setMrTarget] = useState<
    { drawingId: number; pageIndex: number } | null>(null);

  // ---------------------------------------------------------------- v0.47 登录与权限
  // ★ 身份只此一份（store/auth.ts）。这里读它、渲染它，**不再另存一份** ——
  //   两份身份状态必然出现"顶栏显示的是新角色、按钮按旧角色渲染"。
  const { ready: authReady, authEnabled, user: me, setMe } = useAuthStore();
  const isAdmin = useIsAdmin();
  const canEdit = useCanEdit();
  const [pwdOpen, setPwdOpen] = useState(false);

  // 启动第一问：要不要登录、现在是谁。
  // ⚠️ 只在挂载时问一次；之后身份变化一律由各动作自己更新 store
  //   （登录 / 登出 / 改密码）。挂在 `me` 上重问会形成"ask → set → ask"的环。
  useEffect(() => {
    let alive = true;
    fetchMe()
      .then((r) => { if (alive) setMe({ authEnabled: r.auth_enabled, user: r.user, roles: r.roles }); })
      .catch(() => { if (alive) setMe({ authEnabled: true, user: null }); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const doLogout = useCallback(async () => {
    try { await apiLogout(); } catch { /* 退出失败也要把本地身份清掉 */ }
    // 退出=清身份，界面自动切回登录页；顺带把查询缓存清空 ——
    // 否则下一个人登录后会先看到上一个人的图纸列表（服务端取数是对的，
    // 但 React Query 会先渲染缓存，那一瞬间就是数据串人）。
    qc.clear();
    useAuthStore.getState().markLoggedOut();
    selectDrawing(null);
  }, [qc, selectDrawing]);

  // v0.40（用户 2026-10-10 定的顶层结构 `BOM（零件主数据）—— 图纸`）：
  // 顶层**只有一个入口**「零件主数据（BOM）」；图纸是零件的**下一层** ——
  // 从列表里钻取进去（点「图纸」列，或点底部的「未归户图纸」）。
  // ⚠️ v0.38 那两个**平级** tab（图纸工作台 / 零件主数据）已取消：平级意味着
  //    图纸可以不属于任何零件，那正是用户反复说的「不对」。
  // ⚠️ 仍刻意**不引 react-router**：加路由框架要动 main.py 的 SPA 兜底与 dist
  //    基路径，收益不值。用 localStorage 记住选择。
  //
  // v0.65（用户 2026-10-17「在作业指导书页面刷新怎么跳回到主数据了」）：
  //  ★ **`view` 与 `sipEntry` 必须出自同一次读取**，所以先取一份启动快照
  //    `nav`，再由它初始化两个 state。原来只有 `view` 进 localStorage、
  //    而还原白名单里没有 `sip` ⇒ 读到 `sip` 落回 `parts`，接着那个
  //    `setItem(view)` 又把 `parts` 写回去，两次操作把线索一起抹掉。
  //  ★ 为什么这项不能塞进 `useState` 初始化里各读一次：`sip` 这一层没有
  //    目标就渲染不出东西，两个键各读各的会读到**不同时刻**的组合
  //    （上一次的页码 + 上上次的目标），那就是一个错位的页面。
  const [nav] = useState(loadNav);
  const [view, setView] = useState<NavView>(nav.view);

  // ---------------------------------------------------------------- v0.56 用户下拉
  // 用户 2026-10-16 原话：「**账号与权限这个键能不能放到用户下面当设置？**」
  // ⇒ 撤掉顶栏那个独立按键，改成「点用户名 → 一个菜单」：
  //      账号与权限（仅管理员） / 回收站 / 修改密码 / 退出登录
  // ★ 「回收站」也收在这里，而不是再加一个根 tab —— 用户的原话就是要**少几个
  //   顶栏按键**；但删除提示里会明确写「右上角用户名 → 回收站」，可找得到。
  const [userMenu, setUserMenu] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!userMenu) return;
    // 点别处就关；点菜单自己不能关（capture 阶段监听 + contains 判定 ——
    // 这套写法在 `DrawingTree` 的右键菜单上踩过坑，别改成 stopPropagation）。
    const close = (ev: Event) => {
      const t = ev.target as Node | null;
      if (userMenuRef.current && t && userMenuRef.current.contains(t)) return;
      setUserMenu(false);
    };
    const onKey = (ev: KeyboardEvent) => { if (ev.key === 'Escape') setUserMenu(false); };
    window.addEventListener('mousedown', close, true);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', close, true);
      window.removeEventListener('keydown', onKey);
    };
  }, [userMenu]);

  // ---------------------------------------------------------------- v0.50 SIP
  // 「作业指导书 SIP」是一层**需要目标**的页面（SIP 是图纸的产物），所以不能像
  // `users` 那样只靠 `view` 就渲染 —— 必须同时知道"给哪个零件/哪张图纸看"。
  //
  // ★ 入口两条路（用户 2026-10-14 拍板「3、两个都要」）：
  //   · 图纸行 → `{kind:'drawing', id}`，图纸 id 现成的；
  //   · 零件行 → `{kind:'part', id}`，只给零件 id —— 台账**不绑定图纸**
  //     （0 张合法、多张常见），"到底打开哪张"由 SIP 页自己解析
  //     （见 `components/sip/SipPage.tsx` 的图纸解析段）。
  //   ⚠️ 不在两个入口各写一遍"查图纸 → 挑一张"：图纸行那份根本用不上，
  //      写了就是两份实现，迟早只改一处。
  // v0.65：目标与页面一起还原（`nav` 来自 `lib/navState.loadNav`，见上文）。
  const [sipEntry, setSipEntry] = useState<SipEntry | null>(nav.sipEntry);
  const openSip = useCallback((e: SipEntry) => {
    setSipEntry(e);
    setView('sip');
  }, []);
  // v0.65：这一页 + 它的目标**一起**落盘（`saveNav` 内部保证先写目标、再写页码）。
  // ⚠️ 依赖必须同时含 `view` 与 `sipEntry`：从图纸行进 SIP 是两次 setState，
  //    只盯一个会存下「有页面、没目标」的半截状态 —— 那正是本轮修的那个 bug。
  useEffect(() => { saveNav(view, sipEntry); }, [view, sipEntry]);
  /** 从哪来就回哪去 —— 从图纸行进来的回图纸工作台（接着看那张图），
   *  从零件行进来的回零件列表。两种入口各回各的，面包屑也照这个写。 */
  const sipParent: 'workspace' | 'parts' =
    sipEntry?.kind === 'drawing' ? 'workspace' : 'parts';
  // v0.47：`users`（账号与权限）这一层只有管理员进得去，但 localStorage 会
  // **跨会话**记住上次停在哪一页 ⇒ 一个账号被从 admin 降成 viewer 之后，
  // 重进就落在这一层：面包屑写着「账号与权限」、页面却是空的，也没有返回入口，
  // 看起来像"系统坏了"。
  // ⚠️ 不能在 `useState` 初始化里判角色 —— 那一刻身份还没从 /api/auth/me 拉回来，
  //    `isAdmin` 必为 false，会把管理员也一起赶走。只能在**身份就绪之后**纠正。
  // ★ 真正的拦截在后端中间件；这里只是"别把人领进一个他根本用不了的界面"。
  useEffect(() => {
    if (authReady && view === 'users' && !isAdmin) setView('parts');
    if (authReady && view === 'settings' && !isAdmin) setView('parts');
  }, [authReady, view, isAdmin]);

  // v0.61：后台选项字典（量具 / 检验频率 / 客户 / 模具类型 …）在身份就绪后拉一次。
  //   ★ 为什么等 `authReady`：`GET /api/settings/options` 要登录，提前拉必然 401，
  //     白白把 store 打进"降级"态（虽然能靠 FALLBACK 顶上，但界面会误报"内置清单"）。
  //   ★ 拉失败**不影响使用**：store 内部回落到 `lib/annot.ts` 等出厂常量。
  const loadOptions = useOptionsStore((s) => s.load);
  useEffect(() => {
    if (authReady) void loadOptions();
  }, [authReady, loadOptions]);
  // v0.50：`sip` 这一层必须带目标 —— 没有目标就渲染不出东西。
  // v0.65：目标现在**也会被记住**（`lib/navState`），所以这条只剩「兜底」的意思：
  //   万一存储里的目标坏了（被手工改坏、旧版本残留），退回零件列表，
  //   而不是渲染一个永远转圈的空白页。
  useEffect(() => {
    if (view === 'sip' && !sipEntry) setView('parts');
  }, [view, sipEntry]);
  /** 图纸区的范围：某个零件 / 未归户 / 全部。
   *  v0.53：`all` 不再只是兜底 —— 顶栏「图纸」根 tab 就用它直达全部图纸。 */
  const [scope, setScope] = useState<DrawingScope>({ kind: 'all' });
  const openDrawings = useCallback((s: DrawingScope) => {
    setScope(s);
    setView('workspace');
    // 清空选中：否则右侧画布还挂着上一张图（很可能是**别的零件**的），
    // 点「3 张」进来却看到不相干的图纸，会被当成「点了没反应」。
    selectDrawing(null);
  }, [selectDrawing]);
  // v0.38.1：从图纸侧「在零件主数据里查看」跳回列表时要展开的那一行。
  const [partsFocusId, setPartsFocusId] = useState<number | null>(null);
  // v0.56：从**图纸区**点文件夹上的「M 个零件」跳过去时要落到的那个文件夹。
  // ⚠️ 与 `partsFocusId`（展开某一行）刻意分开：一个是"看某个零件的详情行"，
  //    一个是"看某个文件夹下的清单"，合并只会让每个调用点多造一个字面量。
  const [partsFocusFolderId, setPartsFocusFolderId] = useState<number | null>(null);

  // ---------- Ctrl+Z / ⌘Z 撤销删除（全局唯一一份） ----------
  // 删除已改为「按了就删、不弹确认」，撤销就是那层安全网。提示条上的「撤销」
  // 按钮只有 8 秒，快捷键则可以在想起「刚才那个气泡是好的」时随时补救。
  // ⚠️ 只在这里装监听：画布/表格各装一份会导致一次按键触发多次撤销。
  const { undo: undoDelete } = useUndoDelete(selectedDrawingId);
  // v0.47：撤销是**写操作**（后端 `POST .../feature/undo` 要求 engineer）。
  // 只读角色按键不响应 —— 否则按一下 Ctrl+Z 会打到一个注定 403 的接口上，
  // 用户看到的是"撤销失败了"，而真实原因是"你没这个权限"。
  const canEditRef = useRef(canEdit);
  useEffect(() => { canEditRef.current = canEdit; }, [canEdit]);
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!isUndoCombo(e)) return;
      if (isTypingTarget(e.target)) return;   // 输入框里的撤销交给浏览器
      if (!canEditRef.current) return;
      e.preventDefault();
      undoDelete();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [undoDelete]);

  // v0.24：右侧特征表固定宽度（px）+ 可折叠（满屏 Flex，画布自动撑满剩余高度）
  const [tableW, setTableW] = useState<number>(() => {
    const s = Number(localStorage.getItem('gdt_balloon_table_w'));
    return !Number.isNaN(s) && s >= 320 && s <= 900 ? s : 450;
  });
  const [tableCollapsed, setTableCollapsed] = useState<boolean>(
    () => localStorage.getItem('gdt_balloon_table_collapsed') === '1');
  const [resizing, setResizing] = useState(false);
  const twoColRef = useRef<HTMLDivElement>(null);
  const draggingSplitRef = useRef(false);
  const onSplitDown = (e: React.PointerEvent) => {
    if (tableCollapsed) return;
    e.preventDefault();
    draggingSplitRef.current = true;
    setResizing(true);
    (e.currentTarget as Element).setPointerCapture?.(e.pointerId);
  };
  const onSplitMove = (e: React.PointerEvent) => {
    if (!draggingSplitRef.current || !twoColRef.current) return;
    const rect = twoColRef.current.getBoundingClientRect();
    if (rect.width <= 0) return;
    // 由容器右边缘反推右侧面板宽度（指针落在分隔条中心 → 减去半宽 4px）
    const w = rect.right - e.clientX - 4;
    const maxW = Math.max(320, rect.width - 420);   // 保证画布至少留 420px
    setTableW(Math.round(Math.min(Math.max(w, 320), Math.min(900, maxW))));
  };
  const onSplitUp = () => {
    if (draggingSplitRef.current) {
      draggingSplitRef.current = false;
      setResizing(false);
      localStorage.setItem('gdt_balloon_table_w', String(tableW));
    }
  };
  const toggleTablePanel = useCallback(() => {
    setTableCollapsed((c) => {
      const n = !c;
      localStorage.setItem('gdt_balloon_table_collapsed', n ? '1' : '0');
      return n;
    });
  }, []);

  const healthQ = useQuery({ queryKey: ['health'], queryFn: health });

  // v0.32：图纸列表已由左侧的「多级文件夹树」(DrawingTree) 自己取数（/api/folders
  // 一次返回整棵树 + 全部图纸），这里不再另打一份 /api/drawings。

  const drawingQ = useQuery({
    queryKey: ['drawing', selectedDrawingId],
    queryFn: () => getDrawing(selectedDrawingId!),
    enabled: selectedDrawingId !== null,
    // 关键：切图返回时始终向服务端取最新（服务端为权威），避免命中陈旧/空的缓存
    // 导致"加完气泡→切走→切回 图纸上气泡消失"。数据在服务端，拉最新必然正确。
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  /** v0.85：这张图纸归户到哪个物料（未归户时页面要挂一条醒目提示）。
   *  ★ queryKey 与 `DrawingPartChip` **完全一致** ⇒ 共享同一份缓存，
   *    不会多打一次请求；chip 里挂上物料后这里自动变（invalidate 同一个 key）。 */
  const partNoQ = useQuery({
    queryKey: ['drawing-part-no', selectedDrawingId],
    queryFn: () => fetchDrawingPartNo(selectedDrawingId as number),
    enabled: selectedDrawingId !== null,
    refetchOnWindowFocus: false,
  });

  // 联动：点击 Balloon 或详情时，把对应表格行滚动到可视区（v0.21 P0-6/7）
  const scrollToRow = useCallback((pk: number | null) => {
    if (pk == null) return;
    // 延后到选中行渲染后
    setTimeout(() => {
      const el = document.getElementById(`feat-row-${pk}`);
      el?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' });
    }, 40);
  }, []);
  useEffect(() => {
    scrollToRow(selectedFeaturePk);
  }, [selectedFeaturePk, scrollToRow]);

  // v0.22：跨页联动 —— 仅当「选中特征」变化时才自动跳到该特征所在页；
  // 不监听 canvasPageIndex，否则手动点页码会被此 effect 立即拉回，表现为「点2没反应」。
  const canvasPageIndexRef = useRef(canvasPageIndex);
  useEffect(() => { canvasPageIndexRef.current = canvasPageIndex; }, [canvasPageIndex]);
  useEffect(() => {
    if (selectedFeaturePk == null || !drawingQ.data) return;
    const f = drawingQ.data.features.find((x) => x.id === selectedFeaturePk);
    if (!f) return;
    const fPage = f.page_no ?? f.page ?? 0;
    if (fPage !== canvasPageIndexRef.current) setCanvasPage(fPage);
  }, [selectedFeaturePk, drawingQ.data, setCanvasPage]);

  const uploadMut = useMutation({
    // V0.6：上传即返回任务受理；后台识别，前端用 JobProgressPanel 轮询进度。
    // v0.85：**物料随上传一起带上** —— 沈哥 2026-10-22「在分析之前就做归户」。
    //   后端要求 `material_code` 必填（空值 400），所以这里不能再用裸 File 作入参。
    mutationFn: async (arg: { file: File; materialCode: string }) =>
      uploadAsync(arg.file, 'reading', false, false, arg.materialCode),
    onSuccess: (res) => {
      const first = res.jobs?.[0] ?? null;
      setActiveJob(first);
      qc.invalidateQueries({ queryKey: ['folders'] });
      if (first && first.status === 'FAILED') {
        showToast('该文件无法识别（见任务错误）', 'error');
      } else {
        showToast('已开始识别，正在解析…', 'success');
      }
    },
    onError: (e: any) => showToast(`上传失败：${e.message}`, 'error'),
  });

  // 任务到终态的回调：完成则选中图纸并刷新列表；失败仅保留面板（可重试）
  const handleJobDone = useCallback((job: RecognitionJob) => {
    if (job.status === 'COMPLETED' && job.drawing_id != null) {
      // v0.85：识别完只是"有气泡了"，还没挂到物料上 —— 而物料才是后面
      // 工艺路线 / 工序 SIP / 检验任务的起点。所以这句 toast 要直接给出下一步，
      // 否则用户对着画布不知道接着该干什么（沈哥 2026-10-22：「不知道怎么做」）。
      showToast(`识别完成：${job.feature_count} 个特征`
        + ' —— 下一步：点标题下方的「未归户」，把这张图挂到 BOM 物料',
        'success');
      qc.invalidateQueries({ queryKey: ['folders'] });
      qc.invalidateQueries({ queryKey: ['drawing', job.drawing_id] });
      selectDrawing(job.drawing_id);
      // 完成后收起进度面板
      setActiveJob(null);
    }
  }, [qc, selectDrawing, showToast]);

  /** v0.85：选完文件**先不传**，弹「选物料」；物料定了才真正上传。
   *
   *  沈哥 2026-10-22：「图纸必须归户，在分析之前就做归户，省得分析完后找不到归户，
   *  白做分析」—— 一页大图识别要几十秒，挂错物料就整套作废。
   *  ⇒ 顺序从「上传 → 分析 → 事后归户」改成「选物料 → 上传 → 分析」。
   */
  const onPickFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPendingUpload(f);          // 只记下来，等物料选定后再走 `startUpload`
  };

  /** 物料选定后真正发起上传（识别进度由 JobProgressPanel 轮询）。 */
  const startUpload = async (materialCode: string) => {
    const f = pendingUpload;
    if (!f) return;
    setPendingUpload(null);
    setUploading(true);
    setUploadingName(f.name);
    setActiveJob(null);
    try {
      await uploadMut.mutateAsync({ file: f, materialCode });
    } finally {
      setUploading(false);
      setUploadingName(null);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // 栅格 / 大图 OCR 判定：TIF/TIFF 及大型位图走全图 Tile OCR，通常需数十秒到数分钟。
  const uploadingIsRaster = (uploadingName ?? '')
    .match(/\.(tif|tiff|png|jpg|jpeg)$/i) != null;
  // 是否有进行中的识别任务（用于禁用重复上传）
  const jobRunning = activeJob != null &&
    !['COMPLETED', 'FAILED', 'TIMEOUT', 'CANCELLED'].includes(activeJob.status);

  const ocr = healthQ.data?.ocr_available ? '可用' : '未安装';
  const db = healthQ.data?.database?.ok ? '正常' : '异常';

  // 选中特征的主键由 FeatureTable 消费（行高亮 + 展开行）；特征对象本身不再需要
  // —— 原先取它是为了喂给底部详情面板，该面板已移除（v0.31.1）。
  // 点击画布气球：选中（滚动/高亮由上方 effect 处理），同表格行点击
  const handleCanvasFeatureClick = useCallback((f: Feature) => {
    selectFeature(f.id);
  }, [selectFeature]);

  // ---------------------------------------------------------------- v0.47 三道闸
  // ⚠️ 三个 early return 必须放在**全部 hook 之后**（React 的 hooks 规则）。
  //    `App` 的 hook 全在上面，下面只剩 JSX，所以这里是安全的；
  //    以后往本组件末尾再加 hook 时**必须**把它们挪到这三道闸之前。

  // 闸 1：身份还没问回来 —— 不要先渲染登录页。
  // 否则每次刷新都会闪一下登录界面，看起来像"又要登录一次"。
  if (!authReady) {
    return (
      <div className="login-page">
        <div className="login-card login-loading">正在检查登录状态…</div>
      </div>
    );
  }
  // 闸 2：未登录（仅当后端启用了鉴权）。
  if (authEnabled && !me) return <LoginPage />;
  // 闸 3：管理员代设的密码 ⇒ 必须自己改一次才能进。
  // 这一条**不能省**：省掉的话"初始密码只在管理员手里"会一直是常态。
  if (authEnabled && me?.must_change_password) {
    return <PasswordDialog forced />;
  }

  return (
    <div className="app-container">
      {/* ============== 顶栏 ============== */}
      <header className="topbar">
        {/* v0.53 三段式顶栏（用户：「上传图纸放到左边，主数据、作业指导书放到中间」）：
            左 = 品牌 + 上传图纸；中 = 根 tab（主数据 / 图纸 / 作业指导书）；右 = 账号与状态。 */}
        <div className="topbar-left">
        <div className="brand">
          <span className="logo">◎</span>
          <div>
            <div className="brand-name">GDT Balloon AI Web</div>
            <div className="brand-sub">智能 FAI / 检验工程助手 · v0.2</div>
          </div>
        </div>
        <div className="file-zone">
          <input ref={fileRef} type="file"
                 accept=".pdf,.png,.jpg,.jpeg,.tif,.tiff,.bmp,.svg,.dxf,.dwg"
                 hidden onChange={onPickFile} id="fileInput" />
          {/* v0.47：上传/识别是写操作 ⇒ 查看者与检验员不给这个按钮。
              ⚠️ 这只是界面：真正的拦截在后端中间件（`POST /api/drawings/upload*`
              要求 engineer）。界面藏按钮是为了不让人白点。 */}
          {canEdit ? (
            <>
              <span className="file-hint">选择图纸 →</span>
              <button className="btn primary" disabled={uploading || jobRunning}
                      onClick={() => fileRef.current?.click()}>
                {uploading ? '上传中…' : jobRunning ? '识别中…' : '上传并分析'}
              </button>
              {uploading && uploadingIsRaster && (
                <span className="file-hint ocr-note">栅格大图 OCR 较慢，请耐心等待…</span>
              )}
            </>
          ) : (
            <span className="file-hint readonly-hint"
                  title="当前账号是只读角色，不能上传或修改数据">
              只读账号：不能上传 / 修改
            </span>
          )}
        </div>
        </div>{/* /.topbar-left */}
        {/* v0.52（用户 2026-10-15「作业指导书也要跟主数据一样在顶部显示」）：
            顶栏改回**双根 tab 常驻** + 下一层才追加面包屑——
              · 「零件主数据」根：parts 与 workspace（图纸是它的下一层，v0.40 口径不变）；
              · 「作业指导书」根：SIP 升为与主数据同级的一层。没目标时本页给
                「全库图纸选择列表」（`entry.kind='pick'`），选完与图纸入口同行为。
            下一层的「你在哪」仍由 crumb 回答：workspace 显示范围名、sip 显示目标名。 */}
        <nav className="viewtabs crumbnav topnav" aria-label="位置">
          <button className={`viewtab${view === 'parts' ? ' on' : ''}`}
                  onClick={() => setView('parts')}
                  title="零件台账与 BOM（图纸列表是它的下一层）">零件主数据</button>
          {/* v0.53：「图纸」升为**根级按键**（与主数据同级）—— 用户「图纸在主目录
              增加一个类似主数据的按键」。按零件钻取的入口不变（台账行 → 图纸区）。 */}
          <button className={`viewtab${view === 'workspace' ? ' on' : ''}`}
                  onClick={() => openDrawings({ kind: 'all' })}
                  title="全部图纸 —— 上传、识别、归户都在这里">图纸</button>
          <button className={`viewtab${view === 'sip' ? ' on' : ''}`}
                  onClick={() => openSip({ kind: 'pick' })}
                  title="作业指导书 SIP —— 按图纸出检验明细（原材料/制程/成品/出货/自检）">作业指导书</button>
          {/* v0.84（P3）：检验任务 —— SIP 的下游。与「作业指导书」同级：
              任务既不是某张图纸的编辑态，也不属于台账。 */}
          <button className={`viewtab${view === 'tasks' ? ' on' : ''}`}
                  onClick={() => setView('tasks')}
                  title="检验任务 —— 依据 SIP 建单（明细建单时快照），车间按任务逐项测量">检验任务</button>
          {/* v0.61：**免鉴权环境**（本地/开发实例，`GDT_AUTH=0`）下没有用户菜单
              （那个菜单的渲染条件是 `authEnabled && me`），设置页就会没有任何入口
              —— 等于这个功能在开发环境里是死的。
              ⇒ 只在 `!authEnabled` 时补一个顶栏入口；**有鉴权时仍走用户菜单**，
                不在顶栏多加按键（用户 2026-10-16 明确要求「少几个按键」）。 */}
          {isAdmin && !authEnabled && (
            <button className={`viewtab${view === 'settings' ? ' on' : ''}`}
                    onClick={() => setView(view === 'settings' ? 'parts' : 'settings')}
                    title="维护量具与其它选项清单（本实例未启用登录，所有人都是管理员）">
              设置
            </button>
          )}
          {view === 'users' && (
            <>
              <span className="crumb-sep">›</span>
              <span className="crumb-cur">账号与权限</span>
            </>
          )}
          {/* v0.61：设置是「用户菜单」下的另一层，面包屑同样要回答"你在哪"。 */}
          {view === 'settings' && (
            <>
              <span className="crumb-sep">›</span>
              <span className="crumb-cur">设置 · 量具与选项</span>
            </>
          )}
          {/* v0.56：回收站是「用户菜单」下的一层，面包屑要回答"你在哪"。 */}
          {view === 'trash' && (
            <>
              <span className="crumb-sep">›</span>
              <span className="crumb-cur">回收站</span>
            </>
          )}
          {/* v0.53：范围=全部图纸时根 tab 本身就说明位置，面包屑不重复；
              钻取到某零件 / 未归户时才追加，标清「在看谁的图纸」。 */}
          {view === 'workspace' && scope.kind !== 'all' && (
            <>
              <span className="crumb-sep">›</span>
              <span className="crumb-cur">
                {scope.kind === 'unassigned' ? '未归户图纸'
                  : (scope.label ?? `零件 #${scope.id}`)}
              </span>
            </>
          )}
          {view === 'sip' && sipEntry && sipEntry.kind !== 'pick' && sipEntry.label && (
            <>
              <span className="crumb-sep">›</span>
              <span className="crumb-cur" title={sipEntry.label}>{sipEntry.label}</span>
            </>
          )}
        </nav>
        <div className="top-meta">
          <span className="meta-tag">DB: {db}</span>
          <span className="meta-tag">OCR: {ocr}</span>
          {/* ⚠️ 未启用鉴权时**没有用户名按钮** ⇒ 回收站会变得**无处可进**。
              项目里 AUTH_ENABLED 是可关的（为了回归与本地调试），所以这里给一个
              兜底入口 —— 功能不能因为"没登录"就凭空消失。 */}
          {!authEnabled && (
            <button className={`viewtab${view === 'trash' ? ' on' : ''}`}
                    title="删除的图纸 / 零件 / BOM 关系 / 文件夹都在这里，可以恢复"
                    onClick={() => setView(view === 'trash' ? 'parts' : 'trash')}>
              回收站
            </button>
          )}
          {/* v0.56（用户 2026-10-16「账号与权限这个键能不能放到用户下面当设置？」）：
              顶栏那个独立的「账号与权限」按键**已撤**，改成本按钮点开的下拉菜单。 */}
          {authEnabled && me && (
            <div className="user-menu-wrap" ref={userMenuRef}>
              <button className={`user-chip${userMenu ? ' open' : ''}`}
                      aria-haspopup="menu" aria-expanded={userMenu}
                      title={`${me.username}（${roleLabel(me.role)}）—— 点开是账号设置`}
                      onClick={() => setUserMenu((o) => !o)}>
                <span className={`ua-badge ua-${me.role}`}>{roleLabel(me.role)}</span>
                <b>{me.display_name || me.username}</b>
                <span className="chip-caret">▾</span>
              </button>
              {userMenu && (
                <div className="user-menu" role="menu">
                  {isAdmin && (
                    <button className="um-item" role="menuitem"
                            title="管理账号与角色（仅管理员）"
                            onClick={() => {
                              setUserMenu(false);
                              setView(view === 'users' ? 'parts' : 'users');
                            }}>
                      账号与权限
                      {view === 'users' && <span className="um-on">当前</span>}
                    </button>
                  )}
                  {/* v0.61（用户 2026-10-17「增加量具只需要到后台增加」）：
                      量具 / 检验频率 / 客户 / 模具类型… 七份清单在这里维护，
                      气泡图、指导书、测量报告、零件主数据同时跟着变。 */}
                  {isAdmin && (
                    <button className="um-item" role="menuitem"
                            title="维护量具与其它选项清单 —— 改一次，气泡图 / 指导书 / 测量报告同时生效"
                            onClick={() => {
                              setUserMenu(false);
                              setView(view === 'settings' ? 'parts' : 'settings');
                            }}>
                      量具与选项
                      {view === 'settings' && <span className="um-on">当前</span>}
                    </button>
                  )}
                  <button className="um-item" role="menuitem"
                          title="删除的图纸 / 零件 / BOM 关系 / 文件夹都在这里，可以恢复"
                          onClick={() => { setUserMenu(false); setView('trash'); }}>
                    回收站
                    {view === 'trash' && <span className="um-on">当前</span>}
                  </button>
                  <div className="um-sep" />
                  <button className="um-item" role="menuitem"
                          onClick={() => { setUserMenu(false); setPwdOpen(true); }}>
                    修改密码
                  </button>
                  <button className="um-item danger" role="menuitem"
                          onClick={() => { setUserMenu(false); void doLogout(); }}>
                    退出登录
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      {/* ============== 主体 ============== */}
      <main className="main-workspace">
        {/* v0.38：零件主数据（台账）—— 整栏替换，不与工作台并排（它是一次查全量的列表） */}
        {view === 'parts' && (
          <PartMasterPage onToast={(m, t) => showToast(m, t)}
                          focusId={partsFocusId}
                          onFocusConsumed={() => setPartsFocusId(null)}
                          focusFolderId={partsFocusFolderId}
                          onFocusFolderConsumed={() => setPartsFocusFolderId(null)}
                          currentDrawingId={selectedDrawingId}
                          onOpenDrawings={openDrawings}
                          onOpenSip={openSip}
                          canEdit={canEdit} />
        )}
        {/* v0.50：作业指导书 SIP 编辑页。与 `parts`/`users` 不同，它是**整栏替换**
            的一层，且必须带目标（`sipEntry`；没目标时上面那个 effect 会把 view 退回）。 */}
        {view === 'sip' && sipEntry && (
          <SipPage entry={sipEntry} canEdit={canEdit}
                   onToast={(m, t) => showToast(m, t)}
                   onBack={() => setView(sipParent)}
                   onOpenPart={(pmId) => { setPartsFocusId(pmId); setView('parts'); }}
                   onOpenMeasure={(did) => setMrTarget({ drawingId: did, pageIndex: 0 })}
                   onPicked={(did, label, docKind, seq, routeOpId) =>
                     setSipEntry({ kind: 'drawing', id: did, label,
                       docKind, seq, routeOpId })} />
        )}
        {/* v0.84（P3）：检验任务列表。任务明细在建单那一刻从 SIP 快照，
            此后不回查图纸 —— 所以本页只有「看」和「撤销」，没有编辑检验项。 */}
        {view === 'tasks' && (
          <InspectionTasksPage canEdit={canEdit}
                               onToast={(m, t) => showToast(m, t)} />
        )}
        {/* v0.47：账号与权限（仅管理员）。入口按钮也只在 isAdmin 时渲染；
            这里再判一次是**防御**：万一某天上面的按钮条件被改错，
            也不会给非管理员渲染出管理界面（真正的拦截仍在后端）。 */}
        {view === 'users' && isAdmin && (
          <UserAdminPage onToast={(m, t) => showToast(m, t)} />
        )}
        {/* v0.61：设置 · 量具与选项。同样"按钮 + 渲染"两处都判 isAdmin。 */}
        {view === 'settings' && isAdmin && (
          <SettingsPage onToast={(m, t) => showToast(m, t)} />
        )}
        {/* v0.56：回收站（全库：图纸 / 零件行 / BOM 关系 / 文件夹）。
            ★ 只读账号也进得来（列表是 GET = viewer 级），但恢复/彻底删除按钮不给
              —— 后端要 engineer，让人点到注定 403 的按钮只会浪费一次点击。 */}
        {view === 'trash' && (
          <TrashPage onToast={(m, t) => showToast(m, t)} canEdit={canEdit} />
        )}
        {view === 'workspace' && (
        <>
        {/* 左侧栏：图纸列表（**唯一实现**）。v0.40 起由 `scope` 决定是「文件夹树」
            还是「某零件名下的图纸 / 未归户图纸」—— 不再另写第二个列表组件。 */}
        <DrawingTree
          scope={scope}
          selectedDrawingId={selectedDrawingId}
          onSelect={(id, d) => selectDrawing(id, d ?? null)}
          onToast={(m, t) => showToast(m, t)}
          onOpenSip={openSip}
          canEdit={canEdit}
          // v0.56（用户「三者需要联动」）：点夹子上的「M 个零件」跳到零件主数据的
          // **同一个文件夹**（只跳转、不改数据 —— 图纸与零件各归各的）。
          onOpenPartsFolder={(folderId) => {
            setPartsFocusFolderId(folderId);
            setView('parts');
          }}
        />

        {/* 右侧：详情 + Feature Table */}
        <section className="content">
          {!selectedDrawingId && (
            <div className="empty-state">
              <div className="es-icon">◇</div>
              <div className="es-title">选择左侧图纸查看特征</div>
              <div className="es-desc">
                {canEdit ? '或点击右上「上传并分析」开始。' : '当前为只读账号，不能上传或修改。'}
              </div>
            </div>
          )}

          {drawingQ.data && (
            <>
              <div className="detail-head">
                <div>
                  <h2>#{drawingQ.data.id} {drawingQ.data.filename}</h2>
                  <div className="meta">
                    {/* v0.38.1：零件归属栏 —— 图纸侧的「零件主数据」入口。
                        打开图纸即自动按图号匹配；未归户时可就地新建/挑选。
                        ★ 这里没有任何上传图纸的动作，只改「图纸↔零件」的关系。 */}
                    <DrawingPartChip
                      drawingId={drawingQ.data.id}
                      pageIndex={Math.min(canvasPageIndex,
                                          Math.max(0, drawingQ.data.page_count - 1))}
                      partMasterId={drawingQ.data.part_master_id ?? null}
                      onOpenPart={(pmId) => { setPartsFocusId(pmId); setView('parts'); }}
                      onToast={(m, t) => showToast(m, t)} />
                    <span>{drawingQ.data.page_count} 页</span>
                    <span>{drawingQ.data.width.toFixed(1)}×{drawingQ.data.height.toFixed(1)} pt</span>
                    <span>{drawingQ.data.features.length} 个特征</span>
                    {drawingQ.data.page_count > 1 && (
                      <span className="page-selector">
                        页：
                        {Array.from({ length: drawingQ.data.page_count }, (_, i) => i).map(i => (
                          <button key={i}
                                  className={`btn sm ${canvasPageIndex === i ? 'primary' : ''}`}
                                  onClick={() => setCanvasPage(i)}>
                            {i + 1}
                          </button>
                        ))}
                      </span>
                    )}
                  </div>
                </div>
                {/* v0.31：工具条只留「气泡 PDF」。原先的 内部检验报告 / AS9102 FAI /
                    作业指导书 SIP / AI 标题栏 四个入口（含其面板与后端接口）已整体移除。
                    v0.53.3（用户）：①「测量报告」从画布顶栏撤除（SIP 左栏已有唯一入口）；
                    ②「气泡 PDF」改名「导出气泡图」，并与状态徽标合成右侧一组。
                    v0.56（用户 2026-10-16「导出气泡图放到样式旁边」）：**从这里撤走**，
                    改挂到画布工具条的 `toolbarAfterStyle` —— 样式与导出是同一件事
                    （导出的 PDF 用的就是样式面板那 6 项设置，所见即所得），
                    放在一起比隔着一个画布去顶栏找要合理。这一格现在只剩状态徽标。 */}
                <div className="detail-right">
                  <StatusSummary features={drawingQ.data.features} />
                </div>
              </div>

              {/* v0.85：**未归户**横幅。
                  沈哥 2026-10-22：「上传图纸如何和现有的物料绑定，目前操作不知道怎么做」。
                  原来只有一个灰色小签「未归户」混在 meta 行（页数 / 尺寸 / 特征数）里，
                  看不出那是「下一步必须先做的事」—— 而它确实是整条链的起点。 */}
              {partNoQ.data && !partNoQ.data.bound && (
                <div className="sip-alerts warn" style={{ margin: '8px 0 0' }}>
                  <div>
                    ⚠ 这张图纸还没归户到物料 ——
                    <b>归户之后才会带出 BOM 工艺路线 → 工序 SIP → 检验任务</b>。
                  </div>
                  <div style={{ marginTop: 4 }}>
                    怎么做：点上面那个橙色「未归户」按钮 → 输入物料编码 / 零件号 / 名称
                    （例如 PC0960023）→ 点候选项，立刻挂上。
                  </div>
                </div>
              )}

              {/* v0.24：满屏 Flex 工作台 —— 画布列 flex:1 撑满高度，右侧特征表固定宽度可折叠 */}
              <div className={`layout-2col${tableCollapsed ? ' table-collapsed' : ''}${resizing ? ' resizing' : ''}`}
                   ref={twoColRef}
                   style={{ '--table-w': `${tableW}px` } as React.CSSProperties}>
                <div className="col-left">
                  <Canvas drawingId={drawingQ.data.id}
                          widthPt={drawingQ.data.width}
                          heightPt={drawingQ.data.height}
                          // 页码夹取到 [0, page_count-1]：page_no 曾被后端误序列化为
                          // page_id（单页图纸=1），联动时会把画布推到不存在的页。
                          pageIndex={Math.min(canvasPageIndex,
                                              Math.max(0, drawingQ.data.page_count - 1))}
                          imageVersion={drawingQ.data.image_version}
                          toolbarExtra={<FilterSummary features={drawingQ.data.features}
                                                       dropped={drawingQ.data.dropped ?? []} />}
                          // v0.56：导出按钮挪到画布工具条内、紧贴「样式」右侧。
                          // ⚠️ 导出的是**当前画布页**（`canvasPageIndex`）且用**本图纸自己的样式**
                          //    （`loadBalloonStyle(drawingId)`，v0.77）—— 与画布预览同源，所见即所得。
                          toolbarAfterStyle={
                            <button className="btn sm" onClick={async () => {
                              try {
                                const fn = await exportBalloonPdf(
                                  drawingQ.data!.id, canvasPageIndex, false,
                                  loadBalloonStyle(drawingQ.data!.id));
                                showToast(`已导出：${fn}`, 'success');
                              } catch (e: any) {
                                showToast(`导出失败：${e.message}`, 'error');
                              }
                            }} title="导出带气泡的 PDF —— 样式（大小/颜色/粗细/引线/描点/字号）与画布一致；PDF 图纸矢量叠加，栅格图(TIF/PNG)合成叠加">导出气泡图</button>
                          }
                          features={drawingQ.data.features}
                          dpi={150}
                          readOnly={!canEdit}
                          onFeatureClick={handleCanvasFeatureClick} />
                </div>
                <div className="col-divider"
                     title={tableCollapsed ? '展开特征表' : '拖拽调整 画布 / 表格 宽度'}
                     onPointerDown={onSplitDown}
                     onPointerMove={onSplitMove}
                     onPointerUp={onSplitUp}>
                  <button className="cd-toggle"
                          title={tableCollapsed ? '展开特征表' : '折叠特征表'}
                          aria-label={tableCollapsed ? '展开特征表' : '折叠特征表'}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={toggleTablePanel}>
                    {tableCollapsed ? '◂' : '▸'}
                  </button>
                </div>
                <div className={`col-right${tableCollapsed ? ' collapsed' : ''}`}>
                  <FeatureTable drawingId={drawingQ.data.id}
                                features={drawingQ.data.features}
                                readOnly={!canEdit}
                                onChanged={() => {
                                  qc.invalidateQueries({ queryKey: ['drawing', selectedDrawingId] });
                                  qc.invalidateQueries({ queryKey: ['drawings'] });
                                }} />
                </div>
              </div>

              {/* v0.31.1（2026-10-03，用户要求）：原先这里挂着 FeatureDetail 通栏编辑面板 ——
                  它在满屏 Flex 布局之下，选中任一行就挤掉画布约 40% 高度、且每点一行跳一次。
                  已整体移除，编辑能力全部下沉到特征表：要求/公差行内直编、
                  低频字段（量具/精度/单位/备注/偏差/GD&T/状态/删除）收进行尾 ▾ 展开行。 */}
            </>
          )}
        </section>
        </>
        )}
      </main>

      {/* V0.6：统一识别任务进度面板（分步进度 + 失败重试，绝不无限 Loading） */}
      {activeJob && (
        <div className="job-overlay"
             onClick={() => setActiveJob(null)}
             title="点击空白处收起">
          <div className="job-panel"
               onClick={(e) => e.stopPropagation()}>
            <button className="job-close"
                    aria-label="关闭"
                    onClick={() => setActiveJob(null)}>×</button>
            <JobProgressPanel
              jobId={activeJob.id}
              onDone={handleJobDone}
              onCancel={() => setActiveJob(null)}
            />
          </div>
        </div>
      )}

      {/* v0.32：测量报告导出弹窗（先预检 → 核对表头 → 导出 xlsx）。
          v0.52：画布工作台与 SIP 编辑页共用这一个实例 —— 目标由 `mrTarget` 给定
          （SIP 页从表头行唤起时 pageIndex 取 0：附图页放第 1 页的气泡图）。
          ⚠️ 注释里刻意不写那个图纸层的全名：v0.40 护栏裸子串扫它，
          JSX 注释行不在它的跳过名单里，写了就误报（本轮实测踩到）。 */}
      {mrTarget && (
        <MeasureReportDialog
          drawingId={mrTarget.drawingId}
          pageIndex={mrTarget.pageIndex}
          onClose={() => setMrTarget(null)}
          onToast={(m, t) => showToast(m, t)}
        />
      )}

      {/* v0.85：**归户前置**。选了文件但还没定物料 ⇒ 卡在这里，物料定完才上传。
          沈哥 2026-10-22：「在分析之前就做归户，省得分析完后找不到归户，白做分析」。 */}
      {pendingUpload && (
        <MaterialPickDialog
          fileName={pendingUpload.name}
          onCancel={() => {
            setPendingUpload(null);
            if (fileRef.current) fileRef.current.value = '';
          }}
          onPick={(code, label) => {
            showToast(`已挂到物料 ${label}，开始识别…`, 'success');
            void startUpload(code);
          }}
        />
      )}

      {/* v0.47：主动改密码（顶栏「改密」）。强制改密的那条路走上面的闸 3，
          与这里共用同一个组件 —— 两处各写一个表单必然有一处漏掉强度校验。 */}
      {pwdOpen && (
        <PasswordDialog onClose={() => setPwdOpen(false)}
                        onToast={(m, t) => showToast(m, t)} />
      )}

      {toast && (
        <div className={`toast ${toast.type}`}>
          <span className="toast-msg">{toast.msg}</span>
          {toast.action && (
            <button className="toast-act" onClick={toast.action.run}>
              {toast.action.label}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============= 状态汇总 =============
function StatusSummary({ features }: { features: any[] }) {
  const stats: Record<string, number> = {};
  for (const f of features) stats[f.status] = (stats[f.status] || 0) + 1;
  return (
    <div className="status-summary">
      <StatusBadge status="AI_FOUND" count={stats.AI_FOUND || 0} />
      <StatusBadge status="CONFIRMED" count={stats.CONFIRMED || 0} />
      <StatusBadge status="MODIFIED" count={stats.MODIFIED || 0} />
      <StatusBadge status="REJECTED" count={stats.REJECTED || 0} />
    </div>
  );
}
